import { w3cwebsocket as W3CWebSocket } from 'websocket';

import { API_CONFIG } from '../api/config/env';
import WebTokenManager from '../utils/WebTokenManager';

import { ModuleSocketClient, ModuleSocketImpl } from './scanModuleSocket';
import { OrgSocketClient, OrgSocketImpl } from './scanOrgSocket';

const SCAN_WS_BASE = API_CONFIG.SCAN_WS_BASE_URL;
const ORG_CONNECT_TIMEOUT_MS = 5000;
const ORG_CONNECT_MAX_RETRY = 3;
const ORG_CONNECT_BACKOFF_BASE_MS = 500;

const ensureToken = () => {
  const token = WebTokenManager.getToken();
  if (!token) {
    throw new Error('ScanSocket requires an authentication token before attempting to connect.');
  }
  return token;
};

const createModuleSocket = (deviceSerial: string): ModuleSocketClient => {
  const token = ensureToken();
  const url = `${SCAN_WS_BASE}/ws/device/${deviceSerial}?Authorization=Bearer ${token}`;
  return new ModuleSocketImpl(new W3CWebSocket(url));
};

let orgSocket: OrgSocketClient | undefined;
let orgSocketPromise: Promise<OrgSocketClient> | null = null;

const createOrgSocket = () => {
  const token = ensureToken();
  const url = `${SCAN_WS_BASE}/ws/org/?Authorization=Bearer ${token}`;
  return new OrgSocketImpl(new W3CWebSocket(url));
};

const ensureOrgSocket = () => {
  if (!orgSocket) {
    orgSocket = createOrgSocket();
  }
  return orgSocket;
};

const hasConnectedFlag = (socket: OrgSocketClient): socket is OrgSocketClient & { connected: boolean } => {
  return typeof (socket as { connected?: unknown }).connected === 'boolean';
};

const waitForOrgConnection = (socket: OrgSocketClient) => {
  if (hasConnectedFlag(socket) && socket.connected) {
    return Promise.resolve(socket);
  }

  return new Promise<OrgSocketClient>((resolve, reject) => {
    const startedAt = Date.now();

    const poll = () => {
      if (hasConnectedFlag(socket) && socket.connected) {
        resolve(socket);
        return;
      }

      if (Date.now() - startedAt >= ORG_CONNECT_TIMEOUT_MS) {
        reject(new Error('Org socket timed out while connecting.'));
        return;
      }
      setTimeout(poll, 100);
    };

    poll();
  });
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const connectOrgWithRetry = async (attempt: number = 0): Promise<OrgSocketClient> => {
  const socket = ensureOrgSocket();

  try {
    await waitForOrgConnection(socket);
    return socket;
  } catch (error) {
    if (socket instanceof OrgSocketImpl) {
      try {
        socket.disconnect();
      } catch {
        // swallow disconnect errors
      }
    }
    orgSocket = undefined;

    if (attempt < ORG_CONNECT_MAX_RETRY - 1) {
      const backoff = Math.min(ORG_CONNECT_BACKOFF_BASE_MS * Math.pow(2, attempt), ORG_CONNECT_TIMEOUT_MS);
      await delay(backoff);
      return connectOrgWithRetry(attempt + 1);
    }

    throw error;
  }
};

export const ScanSocket = {
  connectModule: (deviceSerial: string): ModuleSocketClient => {
    return createModuleSocket(deviceSerial);
  },
  connectOrg: (): Promise<OrgSocketClient> => {
    if (orgSocket && hasConnectedFlag(orgSocket) && orgSocket.connected) {
      return Promise.resolve(orgSocket);
    }

    if (!orgSocketPromise) {
      orgSocketPromise = connectOrgWithRetry().finally(() => {
        orgSocketPromise = null;
      });
    }

    return orgSocketPromise;
  },
  disconnectOrg: () => {
    if (orgSocket) {
      if (orgSocket instanceof OrgSocketImpl) {
        try {
          orgSocket.disconnect();
        } catch {
          // swallow disconnect errors
        }
      }
      orgSocket = undefined;
    }
  },
  /** 내부 전용: 테스트에서만 사용 */
  __unsafe__resetOrgSocket: () => {
    orgSocket = undefined;
    orgSocketPromise = null;
  },
};
